import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Paperclip, File as FileIcon, X } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { BidiText } from '../../components/common/BidiText';
import { isBrowserRenderableImage } from '../imageRendering';
import { FileSizeDisplay } from './FileSizeDisplay';

interface PendingAttachmentsFieldProps {
  /** Files chosen but not yet uploaded. */
  files: File[];
  /** Add files picked through the paper-clip button. */
  onFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Remove one pending file by index. */
  onRemove: (index: number) => void;
  /** Highlight the field while a drag hovers the surrounding drop target. */
  isDragging?: boolean;
  /** `accept` attribute for the file input. Omit to accept everything. */
  accept?: string;
  maxFiles?: number;
  disabled?: boolean;
}

/**
 * Compact "attach files" field for a quick-create dialog: a paper-clip button,
 * a paste/drop hint, and a thumbnail row of the files waiting to upload.
 *
 * Presentational — the owning form holds the file state (typically via
 * `useClipboardPaste`, whose paste/drop handlers belong on the dialog body so
 * Cmd+V works from anywhere in the form, not just over this field).
 */
export function PendingAttachmentsField({
  files,
  onFileInputChange,
  onRemove,
  isDragging = false,
  accept,
  maxFiles = 10,
  disabled = false,
}: PendingAttachmentsFieldProps) {
  const { t } = useTranslation(['file_management', 'common']);
  const inputRef = useRef<HTMLInputElement>(null);

  // Object URLs are revoked when the file leaves the list (or the field
  // unmounts) — a pasted screenshot is held in memory until then.
  const previews = useMemo(
    () => files.map((file) => (isBrowserRenderableImage(file.type) ? URL.createObjectURL(file) : null)),
    [files]
  );
  useEffect(() => {
    return () => {
      previews.forEach((url) => url && URL.revokeObjectURL(url));
    };
  }, [previews]);

  const atLimit = files.length >= maxFiles;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <Label>{t('file_management:pendingAttachments.label')}</Label>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground touch:h-11 touch:w-11"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || atLimit}
          aria-label={t('file_management:pendingAttachments.attach')}
          title={t('file_management:pendingAttachments.attach')}
          data-testid="attach-files-button"
        >
          <Paperclip className="h-4 w-4" />
        </Button>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={accept}
          onChange={onFileInputChange}
          className="hidden"
          data-testid="attach-files-input"
        />
      </div>

      {files.length === 0 ? (
        <p
          className={cn(
            'rounded-md border border-dashed border-border px-3 py-2 text-xs text-muted-foreground transition-colors',
            isDragging && 'border-module-accent bg-module-accent/15 text-foreground'
          )}
        >
          {t('file_management:pendingAttachments.hint')}
        </p>
      ) : (
        <ul
          className={cn(
            'space-y-1 rounded-md border border-dashed border-transparent transition-colors',
            isDragging && 'border-module-accent bg-module-accent/15'
          )}
        >
          {files.map((file, index) => (
            <li
              key={`${file.name}-${file.lastModified}-${index}`}
              className="flex items-center gap-2 rounded-md border border-border bg-muted/30 p-1.5"
            >
              {previews[index] ? (
                <img
                  src={previews[index] as string}
                  alt={file.name}
                  className="h-9 w-9 shrink-0 rounded object-cover"
                />
              ) : (
                <FileIcon className="h-9 w-9 shrink-0 rounded p-2 text-muted-foreground" />
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm">
                  <BidiText>{file.name}</BidiText>
                </p>
                <FileSizeDisplay bytes={file.size} className="block text-xs text-muted-foreground" />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive touch:h-11 touch:w-11"
                onClick={() => onRemove(index)}
                disabled={disabled}
                aria-label={t('common:app.remove')}
              >
                <X className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
